import { useMemo, useState } from 'react';
import { DataGrid, DataGridColumnHeader, KeenIcon, useDataGrid, DataGridRowSelectAll, DataGridRowSelect } from '@/components';
import { toAbsoluteUrl } from '@/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StoreClientsData } from '.';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
const StoreClients = () => {
  const ColumnInputFilter = ({
    column
  }) => {
    return <Input placeholder="Filter..." value={column.getFilterValue() ?? ''} onChange={event => column.setFilterValue(event.target.value)} className="h-9 w-full max-w-40" />;
  };
  const columns = useMemo(() => [{
    accessorKey: 'id',
    header: () => <DataGridRowSelectAll />,
    cell: ({
      row
    }) => <DataGridRowSelect row={row} />,
    enableSorting: false,
    enableHiding: false,
    meta: {
      headerClassName: 'w-0'
    }
  }, {
    accessorFn: row => row.user,
    id: 'user',
    header: ({
      column
    }) => <DataGridColumnHeader title="Member" filter={<ColumnInputFilter column={column} />} column={column} />,
    enableSorting: true,
    cell: info => <div className="flex items-center gap-2.5">
            <img src={toAbsoluteUrl(`/media/avatars/${info.row.original.user.avatar}`)} className="size-7 rounded-full shrink-0" alt="" />
            <div className="flex flex-col">
              <a className="font-medium text-sm text-gray-900 hover:text-primary-active mb-px" href="#">
                {info.row.original.user.name}
              </a>
              <a className="text-2sm text-gray-700 font-normal hover:text-primary-active" href="#">
                {info.row.original.user.email}
              </a>
            </div>
          </div>,
    meta: {
      headerClassName: 'min-w-[300px]'
    }
  }, {
    accessorFn: row => row.clientId,
    id: 'clientId',
    header: ({
      column
    }) => <DataGridColumnHeader title="Client ID" column={column} />,
    enableSorting: true,
    cell: info => info.row.original.clientId,
    meta: {
      headerClassName: 'min-w-[150px]',
      cellClassName: 'text-gray-800 font-normal'
    }
  }, {
    accessorFn: row => row.ordersValue,
    id: 'ordersValue',
    header: ({
      column
    }) => <DataGridColumnHeader title="Orders Value" column={column} />,
    enableSorting: true,
    cell: info => info.row.original.ordersValue,
    meta: {
      headerClassName: 'min-w-[150px]',
      cellClassName: 'text-gray-800 font-normal'
    }
  }, {
    accessorFn: row => row.location,
    id: 'location',
    header: ({
      column
    }) => <DataGridColumnHeader title="Location" column={column} />,
    enableSorting: true,
    cell: info => <div className="flex items-center gap-1.5">
            <img src={toAbsoluteUrl(`/media/flags/${info.row.original.location.flag}`)} className="size-4 rounded-full shrink-0" alt="" />
            <span className="text-gray-800 font-normal">{info.row.original.location.name}</span>
          </div>,
    meta: {
      headerClassName: 'min-w-[150px]'
    }
  }, {
    accessorFn: row => row.activity,
    id: 'activity',
    header: ({
      column
    }) => <DataGridColumnHeader title="Activity" column={column} />,
    enableSorting: true,
    cell: info => info.row.original.activity,
    meta: {
      headerClassName: 'min-w-[150px]',
      cellClassName: 'text-gray-800 font-normal'
    }
  }, {
    id: 'actions',
    header: ({
      column
    }) => <DataGridColumnHeader title="Invoices" column={column} />,
    enableSorting: true,
    cell: () => <button className="btn btn-link">View</button>,
    meta: {
      headerClassName: 'min-w-[100px]',
      cellClassName: 'text-center'
    }
  }, {
    id: 'dots',
    header: () => '',
    enableSorting: false,
    cell: () => {
      return <button className="btn btn-sm btn-icon btn-clear btn-light">
              <KeenIcon icon="dots-vertical" />
            </button>;
    },
    meta: {
      headerClassName: 'w-[60px]'
    }
  }], []);
  const data = useMemo(() => StoreClientsData, []);
  const handleRowSelection = state => {
    const selectedRowIds = Object.keys(state);
    if (selectedRowIds.length > 0) {
      toast(`Total ${selectedRowIds.length} are selected.`, {
        description: `Selected row IDs: ${selectedRowIds}`,
        action: {
          label: 'Undo',
          onClick: () => console.log('Undo')
        }
      });
    }
  };
  const Toolbar = () => {
    const {
      table
    } = useDataGrid();
    const [searchInput, setSearchInput] = useState('');
    return <div className="card-header flex-wrap gap-2 border-b-0 px-5">
        <h3 className="card-title font-medium text-sm">Showing 10 of 49,053 users</h3>

        <div className="flex flex-wrap gap-2 lg:gap-5">
          <div className="flex">
            <label className="input input-sm">
              <KeenIcon icon="magnifier" />
              <input type="text" placeholder="Search users" value={searchInput} onChange={e => setSearchInput(e.target.value)} />
            </label>
          </div>

          <div className="flex flex-wrap gap-2.5">
            <Select defaultValue="active">
              <SelectTrigger className="w-28" size="sm">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="w-32">
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>

            <Select defaultValue="latest">
              <SelectTrigger className="w-28" size="sm">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent className="w-32">
                <SelectItem value="latest">Latest</SelectItem>
                <SelectItem value="older">Older</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>

            <button className="btn btn-sm btn-outline btn-primary">
              <KeenIcon icon="setting-4" /> Filters
            </button>
          </div>
        </div>
      </div>;
  };
  return <DataGrid columns={columns} data={data} rowSelection={true} onRowSelectionChange={handleRowSelection} pagination={{
    size: 5
  }} sorting={[{
    id: 'user',
    desc: false
  }]} toolbar={<Toolbar />} layout={{
    card: true
  }} />;
};
export { StoreClients };